import {
  CREDIT_SPEND_BATCH,
  type CreditKind,
  type Credits,
  type CreditSpend,
  type Me,
} from '@cribbage/protocol';
import { api } from '~/api/client.js';
import { devicePlayerId, useAuthStore, useDevicePlayerId } from '~/store/authStore.js';
import {
  creditsLeft,
  peopleHintsSpent,
  UNKNOWN_BOOK,
  useCreditStore,
  weekOf,
  type CreditBook,
} from '~/store/creditStore.js';
import { entitlementsOf, useEntitlement } from '~/store/membership.js';
import { newLocalGameId } from './offlineSync.js';
import { flight } from './singleFlight.js';

/**
 * The weekly hint and review meter: what a door asks before it opens, and the
 * sync that keeps the device's count and the server's in step.
 *
 * One credit pays for a hint, each time a new one is shown, or for the first
 * review of a game; every later review door of that game is free
 * (`brain/decisions.md`, 2026-10-08). A guest gets 3 a week and an account 5,
 * for games against bots. The Improver is not counted: `limit` is null, and
 * every door opens without spending.
 *
 * The device spends from its own count (`store/creditStore.ts`) and sends the
 * spends when it can, so a door never waits on the network and a device that
 * goes offline cannot spend past what it had.
 *
 * Against people the weekly meter says nothing about hints. A free player gets
 * none there, and The Improver gets `PEOPLE_HINTS_PER_GAME` in each game,
 * counted on the device per game and sent as spends against people, which the
 * server keeps and never counts against the week.
 */

/** Hints The Improver may see in one game against people (`brain/decisions.md`, 2026-10-08). */
export const PEOPLE_HINTS_PER_GAME = 2;

/** Who the game is against: bots spend from the week, people from the game's own count. */
export type CreditOpponents = CreditSpend['opponents'];

/** Who is spending: the confirmed player, or the one this device last held. */
function spender(): string {
  return devicePlayerId() ?? '';
}

/** Hints left in a game against people: null for a free player, who gets none there. */
function peopleHintsLeft(book: CreditBook, gameKey: string, improver: boolean): number | null {
  if (!improver) return null;
  return Math.max(0, PEOPLE_HINTS_PER_GAME - peopleHintsSpent(book, gameKey));
}

function bookOf(player: string): CreditBook {
  return useCreditStore.getState().books[player] ?? UNKNOWN_BOOK;
}

export interface CreditMeter {
  /** Credits left this week, or null when nobody is counting. */
  readonly left: number | null;
  /** The weekly allowance, or null when nobody is counting. */
  readonly limit: number | null;
  /**
   * When the next week's credits arrive, or null when nobody is counting or
   * the server has never said.
   */
  readonly resetsAt: Date | null;
}

/** The first turnover after `now`: the server's, or a whole number of weeks past it. */
function nextReset(book: CreditBook, now: number): Date | null {
  if (book.limit === null || book.resetsAt === UNKNOWN_BOOK.resetsAt) return null;
  return new Date(weekOf(book.resetsAt, now).end);
}

/**
 * The meter for the player on this device, re-rendering as it changes.
 *
 * Read from the clock when the component renders, so a week that turns over
 * while a screen stays open shows on its next render rather than on the
 * stroke of four.
 */
export function useCredits(): CreditMeter {
  const player = useDevicePlayerId() ?? '';
  const book = useCreditStore((state) => state.books[player]) ?? UNKNOWN_BOOK;
  const now = Date.now();
  return { left: creditsLeft(book, now), limit: book.limit, resetsAt: nextReset(book, now) };
}

/**
 * Hints this game against people has left, re-rendering as they are spent:
 * null when the player gets no hints against people at all.
 */
export function usePeopleHints(gameKey: string): number | null {
  const player = useDevicePlayerId() ?? '';
  const improver = useEntitlement('improver');
  const book = useCreditStore((state) => state.books[player]) ?? UNKNOWN_BOOK;
  return peopleHintsLeft(book, gameKey, improver);
}

/**
 * Whether a table offers the Hint chip, given the game's hints left against
 * people (undefined where no per-game count applies) and whether this
 * position's answer is already kept. A kept answer reopens for free, so its
 * chip stays even once the game's hints are gone.
 */
export function hintOffered(gameLeft: number | undefined, kept: boolean): boolean {
  return gameLeft === undefined || gameLeft > 0 || kept;
}

/** What a door gets when it asks. */
export type CreditAnswer =
  /** A credit is owed, or was spent. */
  | 'spent'
  /** Nothing to spend: nobody is counting, or this game's review is already paid. */
  | 'free'
  /** None left this week. The door opens the out-of-credits sheet instead. */
  | 'none';

export interface CreditAsk {
  readonly kind: CreditKind;
  /**
   * The game this is for: the local record id, or the server's game id. A
   * review is paid once per game.
   */
  readonly gameKey: string;
  /**
   * Who the game is against. A hint against people is counted per game, not
   * per week. Reviews spend from the week either way. Bots when absent.
   */
  readonly opponents?: CreditOpponents | undefined;
}

/** Whether the door may open, without spending anything. */
export function creditAnswer({ kind, gameKey, opponents = 'bots' }: CreditAsk): CreditAnswer {
  const book = bookOf(spender());
  if (kind === 'hint' && opponents === 'people') {
    const improver = entitlementsOf(useAuthStore.getState().user).improver;
    const left = peopleHintsLeft(book, gameKey, improver);
    return left !== null && left > 0 ? 'spent' : 'none';
  }
  if (kind === 'review' && book.reviewed.includes(gameKey)) return 'free';
  const left = creditsLeft(book, Date.now());
  if (left === null) return 'free';
  return left > 0 ? 'spent' : 'none';
}

/** Set while the sync runs: asks it to send now. */
let kick: (() => void) | null = null;

/**
 * Spends a credit for a door that is opening, if one is owed.
 *
 * Call it once the hint or review is actually shown: a hint that failed to
 * arrive costs nothing (`cross-hint`: a failure leaves the balance intact).
 * Answers `none` without spending when the week is used up, or the game's
 * hints against people are.
 */
export function spendCredit(ask: CreditAsk): CreditAnswer {
  const answer = creditAnswer(ask);
  const player = spender();
  const store = useCreditStore.getState();
  if (ask.kind === 'review' && answer !== 'none') store.markReviewed(player, ask.gameKey);
  if (answer !== 'spent') return answer;
  const spend: CreditSpend = {
    id: newLocalGameId(),
    kind: ask.kind,
    game_key: ask.gameKey,
    opponents: ask.kind === 'hint' ? (ask.opponents ?? 'bots') : 'bots',
    spent_at: new Date().toISOString(),
  };
  store.spend(player, spend);
  kick?.();
  return 'spent';
}

/** A send that never settles must not hold every later one back. */
const SEND_TIMEOUT_MS = 10_000;

/** The server's count from `/me`. A server from before the meter says nothing. */
function adopt(user: Me): void {
  const credits = user.credits as Credits | undefined;
  if (credits !== undefined) useCreditStore.getState().adopt(user.id, credits);
}

/**
 * Start keeping the device's meter and the server's in step. Returns the stop.
 *
 * Called once from the app root. `/me` brings the server's count. Spends go up
 * when they are made, when `/me` arrives, when the device comes back online and
 * when the app returns to the foreground. A failed send keeps its spends, with
 * the same ids, for the next of those, so a device with no network makes no
 * requests and a retried send lands once. The spends belong to the player who
 * made them and go up only under that player's session.
 */
export function startCreditSync(): () => void {
  let stopped = false;
  const sends = flight();

  const send = async (): Promise<void> => {
    if (stopped) return;
    const { user, token } = useAuthStore.getState();
    if (user === null || token === null) return;
    const batch = bookOf(user.id).pending.slice(0, CREDIT_SPEND_BATCH);
    if (batch.length === 0) return;
    try {
      const answer = await api.spendCredits(batch, AbortSignal.timeout(SEND_TIMEOUT_MS));
      // Rechecked after the wait: the answer is for the session that sent it.
      if (stopped || useAuthStore.getState().token !== token) return;
      useCreditStore.getState().acknowledge(
        user.id,
        batch.map((spend) => spend.id),
        answer.credits,
      );
      if (bookOf(user.id).pending.length > 0) void sends.after(send);
    } catch {
      // Kept, with the same ids, for the next trigger.
    }
  };

  const sendSoon = (): void => {
    if (!stopped) void sends.after(send);
  };
  kick = sendSoon;

  const stopAuth = useAuthStore.subscribe((state, previous) => {
    if (state.user === null || state.user === previous.user) return;
    adopt(state.user);
    sendSoon();
  });

  const resume = (): void => {
    if (document.visibilityState === 'visible') sendSoon();
  };
  document.addEventListener('visibilitychange', resume);
  globalThis.addEventListener('online', sendSoon);

  const user = useAuthStore.getState().user;
  if (user !== null) adopt(user);
  sendSoon();

  return () => {
    stopped = true;
    if (kick === sendSoon) kick = null;
    stopAuth();
    document.removeEventListener('visibilitychange', resume);
    globalThis.removeEventListener('online', sendSoon);
  };
}
